// frontend/src/components/PasswordStrengthMeter.jsx
import React from "react";
import PropTypes from "prop-types";

/**
 * Four strength levels, lowest → highest. The index into this array is the
 * numeric `level` returned by {@link passwordStrength}.
 */
const LEVELS = ["Weak", "Fair", "Strong", "Very Strong"];

// Tailwind classes per level (bar fill + label text).
const BAR_COLORS = ["bg-red-500", "bg-amber-500", "bg-blue-500", "bg-green-600"];
const TEXT_COLORS = [
  "text-red-600",
  "text-amber-600",
  "text-blue-600",
  "text-green-700",
];

/**
 * Score a password into a 0–3 strength level from length + character-class
 * variety (lowercase / uppercase / digit / special).
 *
 * Exported so the same scoring can be reused (e.g. on a future reset-password
 * form) and unit-tested independently of the React component.
 *
 * @param {string} pw
 * @returns {{ level: number, label: string, percent: number, empty: boolean }}
 */
export function passwordStrength(pw) {
  if (!pw) {
    return { level: 0, label: "", percent: 0, empty: true };
  }

  const variety =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);

  let points = 0;
  if (pw.length >= 10) points += 1; // meets the server minimum
  if (pw.length >= 14) points += 1; // comfortably long
  if (variety >= 2) points += 1;
  if (variety >= 3) points += 1;

  // points 0..4 -> level 0..3
  const level = Math.min(3, points <= 1 ? 0 : points - 1);
  return {
    level,
    label: LEVELS[level],
    percent: ((level + 1) / LEVELS.length) * 100,
    empty: false,
  };
}

/**
 * PasswordStrengthMeter — a real-time, 4-level strength indicator.
 *
 * Renders nothing until the user types. Updates on every keystroke because the
 * parent passes the live `password` value as a prop.
 */
function PasswordStrengthMeter({ password }) {
  const { level, label, empty } = passwordStrength(password);
  if (empty) return null;

  return (
    <div className="mt-2" aria-live="polite">
      <div className="flex gap-1" role="presentation">
        {LEVELS.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full ${
              i <= level ? BAR_COLORS[level] : "bg-gray-200"
            }`}
          />
        ))}
      </div>
      <p className={`mt-1 text-xs font-medium ${TEXT_COLORS[level]}`}>
        Password strength: {label}
      </p>
    </div>
  );
}

PasswordStrengthMeter.propTypes = {
  password: PropTypes.string,
};
PasswordStrengthMeter.defaultProps = {
  password: "",
};

export default PasswordStrengthMeter;
